import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IHotelsResponse } from '@app/models/hotels_response_model';
import { SalesApplication } from '@app/models/purchases.model';
import {
  ReservationRetrieveResp,
  UnAuthReservationLinkRequestPayload,
} from '@app/models/reservations.model';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { HotelsService } from '@app/shared/service/hotels.service';
import {
  LOCALE_EN_US,
  LOCALE_NUMERIC,
  LOCALE_SHORT,
  LOCALE_UTC,
} from '@config/app-default.constant';
import { defaultValues } from '@config/defaultValues.constant';
import { GdsButtonData, GdsButtonTypes } from '@upr-gds-web/components';
import {
  LocalStorageService,
  SessionStorageService,
} from '@upr-web-primer/storage';
import { get } from '@upr-web-primer/udx-utils';
import { DateTime } from 'luxon';
import moment from 'moment';
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HotelReservationCheckInService {
  allHotelList: IHotelsResponse[];
  private readonly isDestroyed$ = new Subject<boolean>();

  constructor(
    @Inject('RelativeUrls') public relativeUrls: any,
    @Inject('AppDefaults') public appDefaults: any,
    private readonly http: HttpClient,
    private readonly localStorage: LocalStorageService,
    private readonly sessionStorage: SessionStorageService,
    private readonly hotelService: HotelsService,
    private readonly common: CommonUtilityService,
  ) {}

  public getInstanceRelativeUrl(url: string, instanceId: string): string {
    return url?.replace('{instance-id}', instanceId);
  }

  public getHotelStayId(...sources: any[]): any {
    for (const source of sources) {
      const stayId =
        get(source, 'hotel_stay.id') ??
        get(source, 'parent_reservation.id') ??
        get(source, 'parent_reservation.hotel_stay_id') ??
        get(source, 'parent_reservation.hotelStayId') ??
        get(source, 'hotelStayId') ??
        get(source, 'hotel_stay_id') ??
        get(source, 'hotelStayUuid') ??
        get(source, 'hotel_stay_uuid') ??
        get(source, 'stayId') ??
        get(source, 'stay_id') ??
        get(source, 'reservationData.hotel_stay.id') ??
        get(source, 'reservationData.parent_reservation.id') ??
        get(source, 'reservationData.hotelStayId') ??
        get(source, 'reservationData.hotel_stay_id') ??
        get(source, 'reservationData.hotelStayUuid') ??
        get(source, 'reservationData.hotel_stay_uuid') ??
        get(source, 'reservationData.stayId') ??
        get(source, 'reservationData.stay_id') ??
        get(source, 'reservationData.id') ??
        get(source, 'id');

      if (stayId) {
        return stayId;
      }
    }

    return undefined;
  }

  public linkThirdPartyHotelReservation(
    guestId: string,
    requestPayload: any,
  ): Observable<any> {
    const authToken = this.localStorage.getItem('oAuthToken');
    let httpHeaders = new HttpHeaders({});
    httpHeaders = httpHeaders.append('Authorization', `Bearer ${authToken}`);
    httpHeaders = httpHeaders.append('X-Source-Id', 'WEB-ACCOUNT');
    const options = { headers: httpHeaders };
    const url = `${environment.oidc.base_url}${this.getVacationRelativeUrl(
      this.relativeUrls.RETRIEVE_THIRDPARTY_RESERVATIONS,
      guestId,
    )}`;

    return this.http.post<any>(url, requestPayload, options);
  }

  public lookupReservationByInstanceId(
    instanceId: string,
    requestPayload: UnAuthReservationLinkRequestPayload,
  ): Observable<ReservationRetrieveResp> {
    const loggedInUserToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ??
      this.localStorage.getItem('oAuthToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${loggedInUserToken}`,
      'Content-Type': 'application/json',
      'X-Source-Id': 'WEB-ACCOUNT',
    });
    const url = `${environment.oidc.base_url}${this.getInstanceRelativeUrl(
      this.relativeUrls.LOOKUP_OHIP_RESERVATION,
      instanceId,
    )}`;

    return this.http
      .post<ReservationRetrieveResp>(url, requestPayload, { headers })
      .pipe(
        map((response) => {
          if (!Array.isArray(response) || !response?.[0]?.hotel_stay) {
            throw new Error('Invalid anonymous reservation lookup response');
          }
          return response;
        }),
      );
  }

  getHotelReservation(guestID?: any): any {
    const loggedInUserToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ??
      this.localStorage.getItem('oAuthToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${loggedInUserToken}`,
    });
    const url =
      environment.oidc?.base_url +
      this.getRelativeUrl(environment.hotelReservationUrl, guestID);
    return this.http.get<any>(url, { headers });
  }

  getVacationsReservationDetails(guestID?: any, reservationId?: any): any {
    const loggedInUserToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ??
      this.localStorage.getItem('oAuthToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${loggedInUserToken}`,
    });
    const url =
      environment.oidc?.base_url +
      this.fetchRelativeUrl(
        this.relativeUrls.TRIP_DETAILS,
        guestID,
        reservationId,
      );
    return this.http.get<any>(url, { headers });
  }

  getHotelStayReservations(guestID?: any): any {
    const loggedInUserToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ??
      this.localStorage.getItem('oAuthToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${loggedInUserToken}`,
    });
    const url =
      environment.oidc?.base_url +
      this.getVacationRelativeUrl(
        this.relativeUrls.VIEW_GUEST_HOTEL_STAYS,
        guestID,
      );
    return this.http.get<any>(url, { headers });
  }

  getOrdersStayReservations(guestID?: any): any {
    const loggedInUserToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ??
      this.localStorage.getItem('oAuthToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${loggedInUserToken}`,
    });
    const ordersUrl = `${this.appDefaults.SECURE_URL}${this.fetchRelativeUrl(
      this.relativeUrls.VIEW_GUEST_ORDERS,
      guestID,
    )}`;
    return this.http.get<any>(ordersUrl, { headers });
  }

  getHotelReservationDetails(guestID?: any, reservationId?: any): any {
    const loggedInUserToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ??
      this.localStorage.getItem('oAuthToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${loggedInUserToken}`,
    });
    const url =
      environment.oidc?.base_url +
      this.getHotelRelativeUrl(
        this.relativeUrls.VIEW_GUEST_HOTEL_STAYS_BY_RESERVATION_ID,
        guestID,
        reservationId,
      );
    return this.http.get<any>(url, { headers });
  }

  getHotelPurchaseByConfirmationForCheckIn(
    guestId: string,
    confirmationNumber: string,
  ): Observable<any> {
    const authToken = this.localStorage.getItem('oAuthToken');
    let httpHeaders = new HttpHeaders({});
    httpHeaders = httpHeaders.append('Authorization', `Bearer ${authToken}`);
    httpHeaders = httpHeaders.append('X-Source-Id', 'WEB-ACCOUNT');
    const options = { headers: httpHeaders };
    const url = `${environment.oidc.base_url}${this.getLinkVacationRelativeUrl(
      this.relativeUrls.VIEW_GUEST_HOTEL_STAYS_BY_RESERVATION_ID,
      guestId,
    )
      .replace('{reservation-id}', confirmationNumber)
      .replace('{resort-area-code}', this.appDefaults.RESORT_AREA_CODE ?? '')}`;

    return this.http.get<any[]>(url, options).pipe(
      map((stays) => {
        const stay = stays?.[0];

        if (!stay) {
          return null;
        }

        return {
          ...stay,
          type: SalesApplication.Hotel,
          order_number:
            stay?.hotel_room_reservations?.[0]?.id ?? confirmationNumber,
          customer_order_id: stay?.customer_order_id ?? stay?.order_id ?? '',
          is_thirdparty: Boolean(stay?.is_thirdparty),
          parent_reservation: stay,
          reservation_index: 0,
        };
      }),
      catchError(() => of(null)),
    );
  }

  getLinkVacationRelativeUrl(url: string, guestId: string): string {
    return url.replace('{guest-id}', guestId);
  }

  convertDateLabel(dateString: string): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString + 'Z');
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const options: Intl.DateTimeFormatOptions = {
      weekday: LOCALE_SHORT,
      month: LOCALE_SHORT,
      day: LOCALE_NUMERIC,
      timeZone: LOCALE_UTC,
    };
    return date.toLocaleDateString(LOCALE_EN_US, options).replace(',', '');
  }

  getTotalNights(startStr: string, endStr: string): string {
    if (!startStr || !endStr) {
      return '';
    }
    const checkinDate = new Date(startStr);
    const checkOutDate = new Date(endStr);
    if (
      Number.isNaN(checkinDate.getTime()) ||
      Number.isNaN(checkOutDate.getTime())
    ) {
      return '';
    }
    const formatDate = (date: Date): string => {
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    };
    const finalStartDate = formatDate(checkinDate);
    const finalEndDate = formatDate(checkOutDate);
    const [startMonth, startDay, startYear] = finalStartDate
      .split('-')
      .map(Number);
    const [endMonth, endDay, endYear] = finalEndDate.split('-').map(Number);

    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);

    const diffMs = endDate.getTime() - startDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return '';
    }
    const pageLabels = this.common.getResolvedPageData();
    return `(${diffDays} ${pageLabels?.['reservation.NightText']}${
      diffDays <= 1 ? '' : 's'
    })`;
  }

  isPastDate(dateStr: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    return date < today;
  }

  public isThirdPartyReservation(...sources: any[]): boolean {
    return sources.some((source) =>
      Boolean(
        source?.is_thirdparty ??
        source?.isThirdParty ??
        source?.parent_reservation?.is_thirdparty ??
        source?.reservationData?.is_thirdparty ??
        source?.reservationData?.parent_reservation?.is_thirdparty,
      ),
    );
  }

  prepareReservationData(hotelBookingDetailArray: any): any {
    return hotelBookingDetailArray?.map((booking: any) => {
      const checkInLabel = this.convertDateLabel(booking.check_in_date);
      const checkOutLabel = this.convertDateLabel(booking.check_out_date);
      const nights = this.getTotalNights(
        booking.check_in_date,
        booking.check_out_date,
      );

      return {
        id: booking?.id,
        hotelName: booking?.hotel_name,
        reservationNumber: booking?.booking_rooms?.[0]?.crs_reservation_id,
        purchaseDate: booking?.purchase_date,
        reservationDate: `${checkInLabel} - ${checkOutLabel} ${nights}`,
        roomCount: booking?.booking_rooms?.length,
        totalCost: booking?.total_with_tax,
        past: booking?.isPastReservation ?? false,
        bookingCurrency: booking?.booking_currency,
        is_thirdparty: this.isThirdPartyReservation(booking),
      };
    });
  }

  updateHotelReservationResponse(
    hotelBookingArray: any[],
    selectedTabIndex?: number,
  ): {
    hotelBookingList: any[];
    upComingHotelBookingList: any[];
    pastHotelBookingList: any[];
  } {
    const pastHotelBookingList = hotelBookingArray
      .filter((item: any) => this.isPastDate(item.check_out_date))
      .map((item: any) => ({
        ...item,
        isPastReservation: true,
      }));

    const upComingHotelBookingList = hotelBookingArray.filter(
      (item: any) => !this.isPastDate(item.check_out_date),
    );

    const hotelBookingList =
      selectedTabIndex === 0
        ? this.prepareReservationData(upComingHotelBookingList)
        : this.prepareReservationData(pastHotelBookingList);

    return {
      hotelBookingList,
      upComingHotelBookingList,
      pastHotelBookingList,
    };
  }

  updateHotelVacationReservationResponse(
    vacationBookingArray: any[],
    selectedTabIndex?: number,
    pageLabels?: any,
    reservationType?: any,
  ): {
    hotelandvacationBookingList: any[];
    upComingHotelVacationBookingList: any[];
    pastVacationandHotelBookingList: any[];
  } {
    const pastVacationandHotelBookingList = vacationBookingArray
      .filter((item: any) => this.isPastDate(item.hotel_stay_period.check_out))
      .map((item: any) => ({
        ...item,
        isPastReservation: true,
      }));

    const upComingHotelVacationBookingList = vacationBookingArray.filter(
      (item: any) => !this.isPastDate(item.hotel_stay_period.check_out),
    );

    const hotelandvacationBookingList =
      selectedTabIndex === 0
        ? this.prepareHotelVacationReservationData(
            upComingHotelVacationBookingList,
            pageLabels,
            reservationType,
          )
        : this.prepareHotelVacationReservationData(
            pastVacationandHotelBookingList,
            pageLabels,
          );

    return {
      hotelandvacationBookingList,
      upComingHotelVacationBookingList,
      pastVacationandHotelBookingList,
    };
  }

  mapHotelImagesWithReservations(bookingcode: any): any {
    this.hotelService.hotelsData$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((hotels) => (this.allHotelList = hotels));

    for (const hotelItem of this.allHotelList || []) {
      if (hotelItem.hotel_code === bookingcode) {
        const imageUrl = hotelItem.content.data.thumbnail_image[0];
        return this.getImage(imageUrl);
      }
    }

    return null;
  }

  getImage(imageurl: any): any {
    return {
      mobile: this.common.isPathAbsolute(imageurl?.mobile[0].src ?? ''),
      tablet: this.common.isPathAbsolute(imageurl?.tablet[0].src ?? ''),
      desktop: this.common.isPathAbsolute(imageurl?.desktop[0].src ?? ''),
      alt: imageurl?.desktop[0].alt_text ?? '',
    };
  }

  updateVacationReservationResponse(
    vacationBookingArray: any[],
    selectedTabIndex?: number,
    pageLabels?: any,
    reservationType?: any,
  ): {
    vacationBookingList: any[];
    upComingVacationBookingList: any[];
    pastVacationBookingList: any[];
  } {
    const pastVacationBookingList = vacationBookingArray
      .filter((item: any) =>
        this.isPastDate(item?.parent_reservation?.hotel_stay_period?.check_out),
      )
      .map((item: any) => ({
        ...item,
        isPastReservation: true,
      }));

    const upComingVacationBookingList = vacationBookingArray.filter(
      (item: any) =>
        !this.isPastDate(
          item?.parent_reservation?.hotel_stay_period?.check_out,
        ),
    );
    const vacationBookingList =
      selectedTabIndex === 0
        ? this.prepareVacationReservationData(
            upComingVacationBookingList,
            pageLabels,
            reservationType,
          )
        : this.prepareVacationReservationData(
            pastVacationBookingList,
            pageLabels,
          );
    return {
      vacationBookingList,
      upComingVacationBookingList,
      pastVacationBookingList,
    };
  }

  prepareVacationReservationData(
    vacationBookingDetailArray: any,
    pagelabels?: any,
    type?: any,
  ): any {
    return vacationBookingDetailArray?.map((booking: any, index: number) => {
      const image = this.mapHotelImagesWithReservations(booking.hotel_code);
      const mobileCheckin = this.setCheckInBtn(
        booking,
        index,
        type,
        pagelabels,
      );
      return {
        id: this.getHotelStayId(booking),
        hotelName: booking?.name,
        reservationNumber: booking?.order_number,
        purchaseDate: booking.purchase_timestamp,
        reservationDate: booking.stay_period,
        totalCost: booking.amount,
        bookingCurrency: booking?.currency_code,
        past: booking?.isPastReservation ?? false,
        hotelcode: booking?.hotel_code,
        image: image,
        mobileCheckin: mobileCheckin,
        is_thirdparty: this.isThirdPartyReservation(booking),
      };
    });
  }

  prepareHotelVacationReservationData(
    vacationBookingDetailArray: any,
    pagelabels: any,
    type?: any,
  ): any {
    if (type === SalesApplication.Hotel) {
      return this.prepareHotelReservationDataFromRooms(
        vacationBookingDetailArray,
        pagelabels,
        type,
      );
    }

    return vacationBookingDetailArray?.map((booking: any, index: number) => {
      const checkInLabel = moment(
        get(booking, 'hotel_stay_period.check_in', '').replace(
          defaultValues.REMOVE_TIMESTAMP,
          '',
        ),
      );
      const checkOutLabel = moment(
        get(booking, 'hotel_stay_period.check_out', '').replace(
          defaultValues.REMOVE_TIMESTAMP,
          '',
        ),
      );
      const image = this.mapHotelImagesWithReservations(booking.hotel_code);
      const mobileCheckin = this.setCheckInBtn(
        booking,
        index,
        type,
        pagelabels,
      );
      return {
        id: this.getHotelStayId(booking),
        hotelName: booking?.hotel_name,
        reservationNumber:
          booking?.in_package === false
            ? booking?.hotel_room_reservations?.[0]?.id
            : booking?.external_references?.[0]?.id,
        purchaseDate: booking.purchase_date,
        reservationDate: `${checkInLabel.format(
          'MM/DD/YYYY',
        )} - ${checkOutLabel.format('MM/DD/YYYY')}
      `,
        totalCost: booking.payment.total_price,
        bookingCurrency: booking?.currency_code,
        past: booking?.isPastReservation ?? false,
        hotelcode: booking?.hotel_code,
        image: image,
        mobileCheckin: mobileCheckin,
        is_thirdparty: this.isThirdPartyReservation(booking),
      };
    });
  }

  prepareHotelReservationDataFromRooms(
    hotelBookingDetailArray: any,
    pagelabels: any,
    type?: any,
  ): any[] {
    return (hotelBookingDetailArray ?? []).flatMap((booking: any) => {
      const roomReservations = booking?.hotel_room_reservations ?? [];
      const rooms = roomReservations.length ? roomReservations : [undefined];
      const checkInLabel = moment(
        get(booking, 'hotel_stay_period.check_in', '').replace(
          defaultValues.REMOVE_TIMESTAMP,
          '',
        ),
      );
      const checkOutLabel = moment(
        get(booking, 'hotel_stay_period.check_out', '').replace(
          defaultValues.REMOVE_TIMESTAMP,
          '',
        ),
      );
      const image = this.mapHotelImagesWithReservations(booking.hotel_code);

      return rooms.map((room: any, reservationIndex: number) => {
        const selectedBooking = {
          ...booking,
          selected_room_reservation: room,
          selected_room_total_price:
            room?.total_price ?? booking?.payment?.total_price,
          reservation_index: reservationIndex,
          is_thirdparty: this.isThirdPartyReservation(room, booking),
        };
        const mobileCheckin = this.setCheckInBtn(
          selectedBooking,
          reservationIndex,
          type,
          pagelabels,
        );

        return {
          ...selectedBooking,
          id: this.getHotelStayId(booking),
          hotelName: booking?.hotel_name,
          reservationNumber: room?.id ?? booking?.external_references?.[0]?.id,
          roomType: room?.room_type_name,
          purchaseDate: booking.purchase_date,
          reservationDate: `${checkInLabel.format(
            'MM/DD/YYYY',
          )} - ${checkOutLabel.format('MM/DD/YYYY')}
      `,
          totalCost: selectedBooking.selected_room_total_price,
          bookingCurrency: booking?.currency_code,
          past: booking?.isPastReservation ?? false,
          hotelcode: booking?.hotel_code,
          image,
          parent_reservation: booking,
          mobileCheckin,
        };
      });
    });
  }

  getHotelReservationImageUrl(pageLabels: any): string {
    const imagePath = pageLabels?.['reservationDetails.Image'];
    return this.common.prependImageUrl(
      environment?.akamai?.tridion + imagePath,
    );
  }

  getHotelUrl(type: 'hotel' | 'vacation'): string {
    const url =
      type === 'hotel'
        ? this.relativeUrls.HOTEL_URL
        : this.relativeUrls.VACATION_PACKAGE_URL;
    return defaultValues.HOTEL_APP_PATH + url;
  }

  setCheckInBtn(
    purchase: any,
    index: any,
    type: any,
    pageLabels: any,
    isView = false,
  ): GdsButtonData {
    if (index === undefined || !purchase) {
      return {};
    }

    const reservationCheck = this.checkInAvailability(
      purchase,
      index,
      type,
      pageLabels,
    );

    if (reservationCheck === 'check_in') {
      const checkInCtaLabel =
        pageLabels?.['com.udx.account.begins_mobile_check_in.cta'] ??
        'Begin Mobile Check-In';

      const obj: GdsButtonData & { reservationData?: any } = {
        type: GdsButtonTypes.tertiary,
        label: checkInCtaLabel,
        analytics: checkInCtaLabel,
        ariaLabel: checkInCtaLabel,
        index: this.getCheckInButtonIndex(purchase, index, type),
        customEventDetail: { type: 'flutter_check_in' },
        reservationData: purchase,
      };

      if (isView) {
        obj.svgOptions = {
          iconName: 'mobile-check-in-black',
          ariaLabel: checkInCtaLabel,
        };
      }

      return obj;
    }

    if (reservationCheck === 'check_out') {
      const checkOutCtaLabel =
        pageLabels?.['com.udx.account.begins_mobile_check_out.cta'] ??
        'Begin Mobile Check-Out';

      const obj: GdsButtonData & { reservationData?: any } = {
        type: GdsButtonTypes.tertiary,
        label: checkOutCtaLabel,
        analytics: checkOutCtaLabel,
        ariaLabel: checkOutCtaLabel,
        index: this.getCheckInButtonIndex(purchase, index, type),
        customEventDetail: { type: 'flutter_check_out' },
        reservationData: purchase,
      };
      if (isView) {
        obj.svgOptions = {
          iconName: 'mobile-check-in-black',
          ariaLabel: checkOutCtaLabel,
        };
      }
      return obj;
    }
    return {};
  }

  private getCheckInButtonIndex(purchase: any, index: any, type: any): any {
    if (type === SalesApplication.Hotel) {
      return purchase?.hotel_room_reservations?.[index]?.id;
    }

    return (
      this.getHotelStayId(purchase) ??
      purchase?.parent_reservation?.hotel_room_reservations?.[0]?.id ??
      purchase?.parent_reservation?.external_references?.[0]?.id ??
      purchase?.order_number
    );
  }

  createBasicLinkReservationsForm(
    formBuilder: FormBuilder,
    emailPattern: string | RegExp,
  ): FormGroup<any> {
    return formBuilder.group({
      confirmationNumber: new FormControl('', [
        Validators.required,
        this.noWhitespaceValidator(),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        this.noWhitespaceValidator(),
      ]),
      email: new FormControl('', [
        Validators.required,
        this.noWhitespaceValidator(),
        Validators.pattern(emailPattern),
      ]),
    });
  }

  createLinkReservationsForm(
    formBuilder: FormBuilder,
    emailPattern: string | RegExp,
  ): FormGroup<any> {
    const form = this.createBasicLinkReservationsForm(
      formBuilder,
      emailPattern,
    );

    form.addControl(
      'checkInDate',
      new FormControl('', [Validators.required, this.noWhitespaceValidator()]),
    );

    return form;
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isWhitespace = (control.value ?? '').trim().length === 0;
      return isWhitespace ? { whitespace: true } : null;
    };
  }

  getLinkReservationDateSelection(
    dateEvent: any,
    pageLabels: any,
  ): { checkInDate: string; errorMessage: string } {
    const isoDate = dateEvent?.detail?.dayInfo?.isoDate;

    if (!isoDate) {
      return {
        checkInDate: '',
        errorMessage: this.getLinkReservationRequiredDateError(pageLabels),
      };
    }

    return {
      checkInDate: this.formatLinkReservationCheckInDate(isoDate),
      errorMessage: '',
    };
  }

  getLinkReservationCalendarLabelData(pageLabels: any): any {
    const chooseDateLabel =
      pageLabels?.['com.udx.choose_a_date.label'] ??
      pageLabels?.['linkReservations.chooseDateLabel'] ??
      'Choose a date';

    return {
      label: { label: chooseDateLabel },
      dropDownControl: {
        labelledBy: 'checkin_date',
        describedBy: 'checkin_date-tooltip',
      },
      modalControlBackground: {
        dropdownControl: {
          labelledBy: 'checkin_date',
          describedBy: 'checkin_date-tooltip',
          modalTitleLabel: chooseDateLabel,
        },
        primaryButtonLabel: pageLabels?.['com.udx.apply.label'] ?? 'Apply',
        secondaryButtonLabel: pageLabels?.['com.udx.reset.label'] ?? 'Reset',
      },
      applyAriaLive:
        pageLabels?.['com.udx.you_must_to_save_changes.label'] ?? '',
    };
  }

  getLinkReservationRequiredDateError(pageLabels: any): string {
    return (
      pageLabels?.['linkReservations.checkInDateEmptyError'] ??
      pageLabels?.['ER26'] ??
      ''
    );
  }

  private formatLinkReservationCheckInDate(isoDate: any): string {
    const isoDateValue = isoDate.toString();
    const datePart = isoDateValue.split('T')[0];

    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }

    const formattedDate = DateTime.fromISO(isoDateValue).toFormat('yyyy-LL-dd');

    return formattedDate !== 'Invalid Date' ? formattedDate : datePart;
  }

  withCheckInReservationData(bookings: any[]): any[] {
    return bookings?.map((booking: any) =>
      booking?.mobileCheckin
        ? {
            ...booking,
            mobileCheckin: {
              ...booking.mobileCheckin,
              reservationData: booking.mobileCheckin.reservationData ?? booking,
              ...(booking.reservation_index !== undefined && {
                reservationIndex: booking.reservation_index,
              }),
            },
          }
        : booking,
    );
  }

  handleAutoRouting(params: {
    upcomingReservations: any[];
    type: SalesApplication;
    pageLabels: any;
    appRoutes: any;
    sessionStorageKey: string;
    returned?: boolean;
    windowRef: any;
    onNoUpcomingReservations: () => void;
  }): void {
    const shouldAutoRoute = this.shouldAutoRouteOnLanding(
      params.sessionStorageKey,
      params.returned,
      params.windowRef,
    );

    if (!shouldAutoRoute) {
      return;
    }

    this.setAutoRoutedOnLanding(params.sessionStorageKey);

    if (!params.upcomingReservations?.length) {
      params.onNoUpcomingReservations();
      return;
    }

    const autoRouteReservation = params.upcomingReservations.find(
      (reservation) =>
        this.isFlutterCheckInButton(
          this.getAutoRouteCheckInButton(
            reservation,
            params.type,
            params.pageLabels,
          ),
        ),
    );

    if (!autoRouteReservation) {
      params.onNoUpcomingReservations();
      return;
    }

    const checkInBtn = this.getAutoRouteCheckInButton(
      autoRouteReservation,
      params.type,
      params.pageLabels,
    );
    const reservationCheck =
      checkInBtn?.customEventDetail?.type === 'flutter_check_out'
        ? 'check_out'
        : 'check_in';
    this.routeToCheckIn(
      checkInBtn?.reservationData ?? autoRouteReservation,
      reservationCheck,
      params.appRoutes,
      params.pageLabels,
    );
  }

  private getAutoRouteCheckInButton(
    reservation: any,
    type: SalesApplication,
    pageLabels: any,
  ): GdsButtonData & { reservationData?: any } {
    if (this.isFlutterCheckInButton(reservation?.mobileCheckin)) {
      return reservation.mobileCheckin;
    }

    return this.setCheckInBtn(
      reservation,
      reservation?.reservation_index ?? 0,
      type,
      pageLabels,
    );
  }

  private isFlutterCheckInButton(checkInBtn: any): boolean {
    return (
      checkInBtn?.customEventDetail?.type === 'flutter_check_in' ||
      checkInBtn?.customEventDetail?.type === 'flutter_check_out'
    );
  }

  shouldAutoRouteOnLanding(
    sessionStorageKey: string,
    returned: boolean | undefined,
    windowRef: any,
  ): boolean {
    if (this.hasAutoRoutedOnLanding(sessionStorageKey) || returned) {
      return false;
    }

    const referrer = windowRef?.document?.referrer ?? '';
    const currentHost = windowRef?.location?.host ?? '';
    const isFromProfile = Boolean(windowRef?.history?.state?.from_profile);
    const isFromExternal = referrer === '';
    const isFromOwnDomain = Boolean(
      currentHost && referrer.includes(currentHost),
    );

    return isFromProfile || isFromExternal || isFromOwnDomain;
  }

  hasAutoRoutedOnLanding(sessionStorageKey: string): boolean {
    return this.sessionStorage.getItem(sessionStorageKey) === 'true';
  }

  setAutoRoutedOnLanding(sessionStorageKey: string): void {
    this.sessionStorage.setItem(sessionStorageKey, 'true');
  }

  getCheckInStayId(buttonData: any): any {
    const purchase = buttonData?.reservationData;

    return (
      this.getHotelStayId(purchase, buttonData) ??
      purchase?.order_number ??
      buttonData?.index
    );
  }

  private routeToCheckIn(
    reservation: any,
    reservationCheck: string,
    appRoutes: any,
    pageLabels: any,
  ): void {
    const state = {
      stayId: this.getHotelStayId(reservation) ?? reservation?.order_number,
      checkingOut: reservationCheck !== 'check_in',
      eligibleHotels: this.getEligibleHotelLabels(pageLabels),
    };

    this.common.changeRoute(
      appRoutes?.HOTELS_CHECK_IN,
      { flutterMobile: true },
      undefined,
      state,
    );
  }

  shouldAutoRouteToCheckIn(params: {
    retrieved?: boolean;
    returned?: boolean;
    checkInBtn?: GdsButtonData;
    reservation?: any;
    type?: any;
    pageLabels?: any;
  }): boolean {
    const reservationSource = this.getCheckInReservationSource(
      params.reservation,
      params.type ?? SalesApplication.Hotel,
    );

    return (
      Boolean(params.retrieved) &&
      !params.returned &&
      !reservationSource?.is_thirdparty &&
      Boolean(reservationSource?.hotel_stay_period?.check_in) &&
      (params.checkInBtn?.customEventDetail?.type === 'flutter_check_in' ||
        params.checkInBtn?.customEventDetail?.type === 'flutter_check_out')
    );
  }

  getEligibleHotelLabels(pageLabels: any): string {
    const sourceEligibleHotels =
      pageLabels?.['com.udx.hotels.eligible_for_checkin'] ??
      get(pageLabels, 'com.udx.hotels.eligible_for_checkin');
    const targetEligibleHotels =
      pageLabels?.['hotels.eligible_for_checkin'] ??
      get(pageLabels, 'hotels.eligible_for_checkin');

    return sourceEligibleHotels ?? targetEligibleHotels ?? '';
  }

  checkInAvailability(
    reservations: any,
    index: number,
    type: any,
    pageLabels: any,
  ): any {
    const reservationSource = this.getCheckInReservationSource(
      reservations,
      type,
    );
    const hotelCode = reservationSource?.hotel_code ?? reservations?.hotel_code;
    const eligibleForCheckIn = this.getEligibleHotelLabels(pageLabels);
    if (!hotelCode || !eligibleForCheckIn.includes(hotelCode)) {
      return 'none';
    }

    const zone = this.appDefaults.UPR_TIME_ZONE;
    const now = DateTime.local().setZone(zone);
    const { stayCheckIn, stayCheckOut, purchaseDated } = this.getCheckInDates(
      reservations,
      zone,
      type,
    );
    const { isCheckedIn, isCheckedOut } = this.getCheckInStatus(
      reservations,
      index,
      type,
    );

    if (
      this.shouldHideCheckIn(
        isCheckedIn,
        isCheckedOut,
        stayCheckIn,
        now,
        purchaseDated,
      )
    ) {
      return 'none';
    }

    if (this.canCheckIn(isCheckedIn, stayCheckIn, now)) {
      return 'check_in';
    }

    if (this.canCheckOut(isCheckedIn, isCheckedOut, stayCheckOut, now)) {
      return 'check_out';
    }

    return 'none';
  }

  sortByNearestCheckIn(data: any[], type: string): any[] {
    const today = DateTime.now().startOf('day').toMillis();

    const getCheckIn = (item: any): number => {
      const date =
        type === 'HOTEL-ICE'
          ? item?.hotel_stay_period?.check_in
          : item?.parent_reservation?.hotel_stay_period?.check_in;

      return DateTime.fromISO(date).toMillis();
    };
    const getCheckOut = (item: any): number => {
      const date =
        type === 'HOTEL-ICE'
          ? item?.hotel_stay_period?.check_out
          : item?.parent_reservation?.hotel_stay_period?.check_out;

      return DateTime.fromISO(date).toMillis();
    };
    const sorted = [...data].sort((a, b) => getCheckIn(a) - getCheckIn(b));

    const upcoming = sorted.filter((item) => getCheckOut(item) >= today);

    const past = sorted.filter((item) => getCheckOut(item) < today).reverse();

    return [...upcoming, ...past];
  }

  private getRelativeUrl(url: string, guestId: string): string {
    return url?.replace('{guestId}', guestId);
  }

  private fetchRelativeUrl(
    url: string,
    guestID?: any,
    reservationId?: any,
  ): string {
    return url
      .replace('{guest-id}', guestID)
      .replace('{trip-id}', reservationId);
  }

  private getVacationRelativeUrl(url: string, guestId: string): string {
    return url.replace('{guest-id}', guestId);
  }

  private getHotelRelativeUrl(
    url: string,
    guestID?: any,
    reservationId?: any,
  ): string {
    return url
      ?.replace('{guest-id}', guestID)
      ?.replace('{reservation-id}', reservationId)
      ?.replace('{resort-area-code}', this.appDefaults.RESORT_AREA_CODE);
  }

  private getCheckInReservationSource(reservations: any, type: any): any {
    return type === SalesApplication.Hotel
      ? reservations
      : (reservations?.parent_reservation ?? reservations);
  }

  private getCheckInDates(
    reservations: any,
    zone: string,
    type: any,
  ): { stayCheckIn: any; stayCheckOut: any; purchaseDated: any } {
    const source = this.getCheckInReservationSource(reservations, type);
    const stayCheckIn = DateTime.fromISO(source?.hotel_stay_period?.check_in, {
      zone: 'utc',
    })
      .setZone(zone, { keepLocalTime: true })
      .set({ hour: 16, minute: 0, second: 0, millisecond: 0 });

    const stayCheckOut = DateTime.fromISO(
      source?.hotel_stay_period?.check_out,
      { zone: 'utc' },
    ).setZone(zone, { keepLocalTime: true });

    const purchaseDated = DateTime.fromISO(source?.purchase_date).setZone(
      zone,
      { keepLocalTime: true },
    );

    return { stayCheckIn, stayCheckOut, purchaseDated };
  }

  private getCheckInStatus(
    reservations: any,
    index: number,
    type: any,
  ): { isCheckedIn: boolean; isCheckedOut: boolean } {
    const reservationSource = this.getCheckInReservationSource(
      reservations,
      type,
    );
    const roomReservations = reservationSource?.hotel_room_reservations ?? [];

    if (type === SalesApplication.Hotel) {
      const roomReservation = roomReservations[index] ?? roomReservations[0];
      return {
        isCheckedIn: !!roomReservation?.is_checked_in,
        isCheckedOut: !!roomReservation?.is_checked_out,
      };
    }

    return {
      isCheckedIn:
        roomReservations.length > 0 &&
        roomReservations.every(
          (reservation: any) => !!reservation.is_checked_in,
        ),
      isCheckedOut:
        roomReservations.length > 0 &&
        roomReservations.every(
          (reservation: any) => !!reservation.is_checked_out,
        ),
    };
  }

  private shouldHideCheckIn(
    isCheckedIn: boolean,
    isCheckedOut: boolean,
    stayCheckIn: any,
    now: any,
    purchaseDated: any,
  ): boolean {
    return (
      (isCheckedIn && isCheckedOut) ||
      (stayCheckIn.hasSame(now, 'day') &&
        purchaseDated.hasSame(stayCheckIn, 'day'))
    );
  }

  private canCheckIn(
    isCheckedIn: boolean,
    stayCheckIn: any,
    now: any,
  ): boolean {
    if (isCheckedIn) {
      return false;
    }

    const hoursUntilCheckIn = stayCheckIn.diff(now, 'hours').hours;
    const within72Hours = hoursUntilCheckIn >= 0 && hoursUntilCheckIn <= 72;
    const isCheckInDay = now.hasSame(stayCheckIn, 'day');
    const before930pm = now.hour < 21 || (now.hour === 21 && now.minute < 30);

    return within72Hours || (isCheckInDay && before930pm);
  }

  private canCheckOut(
    isCheckedIn: boolean,
    isCheckedOut: boolean,
    stayCheckOut: any,
    now: any,
  ): boolean {
    return isCheckedIn && !isCheckedOut && now.hasSame(stayCheckOut, 'day');
  }
}
